import { darkTheme } from 'naive-ui'

const THEME_KEY = 'komunily-admin-theme'

// Helper to get the correct window object
const getWindow = () => {
	if (typeof window !== 'undefined') {
		return window
	}
	return null
}

// Helper to detect system theme preference
const getSystemTheme = () => {
	const win = getWindow()
	if (!win) return 'light'

	if (win.matchMedia && win.matchMedia('(prefers-color-scheme: dark)').matches) {
		return 'dark'
	}
	return 'light'
}

// Helper to get saved theme from localStorage
const getSavedTheme = () => {
	const win = getWindow()
	if (!win) return null

	try {
		return win.localStorage.getItem(THEME_KEY)
	} catch (e) {
		console.error('Error reading theme from localStorage:', e)
		return null
	}
}

// Helper to save theme to localStorage
const saveTheme = (theme) => {
	const win = getWindow()
	if (!win) return

	try {
		win.localStorage.setItem(THEME_KEY, theme)
	} catch (e) {
		console.error('Error saving theme to localStorage:', e)
	}
}

// Initialize theme
const initializeTheme = () => {
	const savedTheme = getSavedTheme()
	if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
		return savedTheme
	}
	return 'system'
}

// Apply theme to HTML element
const applyThemeToDOM = (isDark) => {
	const win = getWindow()
	if (!win) return

	const htmlElement = win.document.documentElement
	if (isDark) {
		htmlElement.classList.add('dark')
	} else {
		htmlElement.classList.remove('dark')
	}
}

const state = {
	theme: initializeTheme(),
	systemTheme: getSystemTheme(),
	isSystemTheme: false
}

const mutations = {
	SET_THEME(state, theme) {
		state.theme = theme
		state.isSystemTheme = theme === 'system'
		saveTheme(theme)
	},
	SET_SYSTEM_THEME(state, systemTheme) {
		state.systemTheme = systemTheme
	}
}

const actions = {
	initTheme({ commit, getters }) {
		const theme = state.theme
		commit('SET_THEME', theme)

		// Apply initial theme
		applyThemeToDOM(getters.isDark)

		// Listen for system theme changes
		const win = getWindow()
		if (win && win.matchMedia) {
			const mediaQuery = win.matchMedia('(prefers-color-scheme: dark)')

			const handleChange = (e) => {
				commit('SET_SYSTEM_THEME', e.matches ? 'dark' : 'light')
				if (state.isSystemTheme) {
					applyThemeToDOM(e.matches)
				}
			}

			// Modern browsers
			if (mediaQuery.addEventListener) {
				mediaQuery.addEventListener('change', handleChange)
			} else if (mediaQuery.addListener) {
				// Fallback for older browsers
				mediaQuery.addListener(handleChange)
			}
		}
	},

	setTheme({ commit, getters }, theme) {
		commit('SET_THEME', theme)
		applyThemeToDOM(getters.isDark)
	},

	toggleTheme({ state, dispatch, getters }) {
		let newTheme

		if (state.theme === 'light') {
			newTheme = 'dark'
		} else if (state.theme === 'dark') {
			newTheme = 'system'
		} else {
			// system theme - toggle to opposite of current system preference
			newTheme = getters.isDark ? 'light' : 'dark'
		}

		dispatch('setTheme', newTheme)
	}
}

const getters = {
	currentTheme: (state) => state.theme,

	isDark: (state) => {
		if (state.theme === 'dark') return true
		if (state.theme === 'light') return false
		// System theme
		return state.systemTheme === 'dark'
	},

	isLight: (state, getters) => !getters.isDark,

	naiveTheme: (state, getters) => {
		return getters.isDark ? darkTheme : null
	},

	themeOverrides: (state, getters) => {
		const isDark = getters.isDark

		// Base colors that match main.css CSS variables
		const lightColors = {
			// Primary colors
			primaryColor: '#1355ff',
			primaryColorHover: '#4c7cff',
			primaryColorPressed: '#0d47e6',
			primaryColorSuppl: '#1355ff',

			// Status colors
			successColor: '#00ae4d',
			successColorHover: '#00c759',
			successColorPressed: '#009a44',
			successColorSuppl: '#00ae4d',

			warningColor: '#ffc000',
			warningColorHover: '#ffcc33',
			warningColorPressed: '#e6ac00',
			warningColorSuppl: '#ffc000',

			errorColor: '#ff5271',
			errorColorHover: '#ff6b87',
			errorColorPressed: '#e64966',
			errorColorSuppl: '#ff5271',

			infoColor: '#1355ff',
			infoColorHover: '#4c7cff',
			infoColorPressed: '#0d47e6',
			infoColorSuppl: '#1355ff',

			// Background colors matching CSS variables
			bodyColor: '#ffffff',
			cardColor: '#ffffff',
			modalColor: '#ffffff',
			popoverColor: '#ffffff',
			tableColor: '#ffffff',

			// Input colors
			inputColor: '#ffffff',
			inputColorDisabled: '#fafafb',

			// Border colors
			borderColor: '#e0e2e9',
			tableBorderColor: '#e0e2e9',

			// Text colors
			textColorBase: '#07090d',
			textColor1: '#07090d',
			textColor2: '#3c4353',
			textColor3: '#818a9c',
			textColorDisabled: '#818a9c',
			placeholderColor: '#818a9c',

			// Hover colors
			hoverColor: '#e7eaee',
			tableHeaderColor: '#fafafb',

			// Scrollbar
			scrollbarColor: '#e0e2e9',
			scrollbarColorHover: '#3c4353'
		}

		const darkColors = {
			// Primary colors
			primaryColor: '#4c7cff',
			primaryColorHover: '#6b8fff',
			primaryColorPressed: '#3366ff',
			primaryColorSuppl: '#4c7cff',

			// Status colors
			successColor: '#00d962',
			successColorHover: '#33e47a',
			successColorPressed: '#00b64f',
			successColorSuppl: '#00d962',

			warningColor: '#ffcc33',
			warningColorHover: '#ffd659',
			warningColorPressed: '#e6b82e',
			warningColorSuppl: '#ffcc33',

			errorColor: '#ff6b7d',
			errorColorHover: '#ff8896',
			errorColorPressed: '#e66070',
			errorColorSuppl: '#ff6b7d',

			infoColor: '#4c7cff',
			infoColorHover: '#6b8fff',
			infoColorPressed: '#3366ff',
			infoColorSuppl: '#4c7cff',

			// Background colors matching CSS variables
			bodyColor: '#151f33',
			cardColor: '#1e293b',
			modalColor: '#1e293b',
			popoverColor: '#1e293b',
			tableColor: '#151f33',

			// Input colors
			inputColor: '#1e293b',
			inputColorDisabled: '#141d2f',

			// Border colors
			borderColor: '#475569',
			tableBorderColor: '#475569',

			// Text colors
			textColorBase: '#ffffff',
			textColor1: '#ffffff',
			textColor2: '#e0e2e9',
			textColor3: '#92929d',
			textColorDisabled: '#92929d',
			placeholderColor: '#92929d',

			// Hover colors
			hoverColor: '#141d2f',
			tableHeaderColor: '#1e293b',

			// Scrollbar
			scrollbarColor: '#475569',
			scrollbarColorHover: '#64748b'
		}

		const colors = isDark ? darkColors : lightColors

		return {
			common: colors,

			// Button component
			Button: {
				textColor: colors.textColor1,
				textColorHover: colors.textColor1,
				textColorPressed: colors.textColor1,
				textColorFocus: colors.textColor1,
				textColorDisabled: colors.textColorDisabled,
				border: `1px solid ${colors.borderColor}`,
				borderHover: `1px solid ${colors.primaryColorHover}`,
				borderPressed: `1px solid ${colors.primaryColorPressed}`,
				borderFocus: `1px solid ${colors.primaryColor}`,
				borderDisabled: `1px solid ${colors.borderColor}`,
				color: 'transparent',
				colorHover: colors.hoverColor,
				colorPressed: colors.hoverColor,
				colorFocus: 'transparent',
				colorDisabled: colors.inputColorDisabled
			},

			// Card component
			Card: {
				color: colors.cardColor,
				colorModal: colors.modalColor,
				colorTarget: colors.cardColor,
				colorEmbedded: colors.cardColor,
				textColor: colors.textColor1,
				titleTextColor: colors.textColor1,
				borderColor: colors.borderColor,
				actionColor: colors.hoverColor
			},

			// Input components (also used by InputGroupLabel)
			Input: {
				color: colors.inputColor,
				colorDisabled: colors.inputColorDisabled,
				colorFocus: colors.inputColor,
				textColor: colors.textColor1,
				textColorDisabled: colors.textColorDisabled,
				placeholderColor: colors.placeholderColor,
				placeholderColorDisabled: colors.textColorDisabled,
				border: `1px solid ${colors.borderColor}`,
				borderHover: `1px solid ${colors.primaryColorHover}`,
				borderDisabled: `1px solid ${colors.borderColor}`,
				borderFocus: `1px solid ${colors.primaryColor}`,
				boxShadowFocus: `0 0 0 2px ${colors.primaryColor}40`,
				caretColor: colors.primaryColor,
				// InputGroupLabel gets its theme from Input
				groupLabelColor: colors.inputColor,
				groupLabelBorder: `1px solid ${colors.borderColor}`,
				groupLabelTextColor: colors.textColor2
			},

			// Select component
			Select: {
				peers: {
					InternalSelection: {
						color: colors.inputColor,
						colorActive: colors.inputColor,
						colorDisabled: colors.inputColorDisabled,
						textColor: colors.textColor1,
						textColorDisabled: colors.textColorDisabled,
						placeholderColor: colors.placeholderColor,
						placeholderColorDisabled: colors.textColorDisabled,
						border: `1px solid ${colors.borderColor}`,
						borderActive: `1px solid ${colors.primaryColor}`,
						borderFocus: `1px solid ${colors.primaryColor}`,
						borderHover: `1px solid ${colors.primaryColorHover}`,
						borderDisabled: `1px solid ${colors.borderColor}`,
						boxShadowActive: `0 0 0 2px ${colors.primaryColor}40`,
						boxShadowFocus: `0 0 0 2px ${colors.primaryColor}40`,
						caretColor: colors.primaryColor
					},
					InternalSelectMenu: {
						color: colors.popoverColor,
						optionColorHover: colors.hoverColor,
						optionColorActive: colors.primaryColor,
						optionTextColor: colors.textColor1,
						optionTextColorActive: '#ffffff',
						optionTextColorHover: colors.textColor1,
						groupHeaderTextColor: colors.textColor2
					}
				}
			},

			// DataTable component
			DataTable: {
				thColor: colors.tableHeaderColor,
				thColorHover: colors.hoverColor,
				tdColor: colors.tableColor,
				tdColorHover: colors.hoverColor,
				tdColorStriped: colors.hoverColor,
				borderColor: colors.tableBorderColor,
				thTextColor: colors.textColor1,
				tdTextColor: colors.textColor1,
				thFontWeight: '600'
			},

			// Layout component
			Layout: {
				color: colors.bodyColor,
				siderColor: colors.cardColor,
				siderBorderColor: colors.borderColor,
				siderToggleButtonColor: colors.hoverColor,
				siderToggleButtonIconColor: colors.textColor1,
				siderToggleBarColor: colors.borderColor
			},

			// Menu component
			Menu: {
				color: colors.cardColor,
				itemColorHover: colors.hoverColor,
				itemColorActive: colors.primaryColor,
				itemColorActiveHover: colors.primaryColorHover,
				itemTextColor: colors.textColor1,
				itemTextColorHover: colors.textColor1,
				itemTextColorActive: '#ffffff',
				itemTextColorActiveHover: '#ffffff',
				itemTextColorChildActive: colors.primaryColor,
				itemIconColor: colors.textColor2,
				itemIconColorHover: colors.textColor1,
				itemIconColorActive: '#ffffff',
				itemIconColorActiveHover: '#ffffff',
				itemIconColorChildActive: colors.primaryColor,
				groupTextColor: colors.textColor2,
				arrowColor: colors.textColor3,
				arrowColorHover: colors.textColor2,
				arrowColorActive: '#ffffff',
				arrowColorActiveHover: '#ffffff',
				arrowColorChildActive: colors.primaryColor
			},

			// Modal component
			Modal: {
				color: colors.modalColor,
				textColor: colors.textColor1,
				boxShadow: isDark
					? '0 6px 16px -9px rgba(0, 0, 0, 0.24), 0 9px 28px 0 rgba(0, 0, 0, 0.15), 0 12px 48px 16px rgba(0, 0, 0, 0.09)'
					: '0 6px 16px -9px rgba(0, 0, 0, 0.08), 0 9px 28px 0 rgba(0, 0, 0, 0.05), 0 12px 48px 16px rgba(0, 0, 0, 0.03)'
			},

			// Popover component
			Popover: {
				color: colors.popoverColor,
				textColor: colors.textColor1,
				borderColor: colors.borderColor,
				boxShadow: isDark
					? '0 3px 6px -4px rgba(0, 0, 0, 0.36), 0 6px 16px 0 rgba(0, 0, 0, 0.24), 0 9px 28px 8px rgba(0, 0, 0, 0.15)'
					: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)'
			},

			// Switch component
			Switch: {
				railColor: colors.borderColor,
				railColorActive: colors.primaryColor,
				buttonColor: '#ffffff',
				buttonColorActive: '#ffffff',
				textColor: colors.textColor1,
				loadingColor: colors.primaryColor
			},

			// Form components
			Form: {
				labelTextColor: colors.textColor1,
				asteriskColor: colors.errorColor,
				feedbackTextColorError: colors.errorColor,
				feedbackTextColorWarning: colors.warningColor
			},

			// Pagination component
			Pagination: {
				itemColor: 'transparent',
				itemColorHover: colors.hoverColor,
				itemColorPressed: colors.hoverColor,
				itemColorActive: colors.primaryColor,
				itemColorActiveHover: colors.primaryColorHover,
				itemTextColor: colors.textColor1,
				itemTextColorHover: colors.textColor1,
				itemTextColorPressed: colors.textColor1,
				itemTextColorActive: '#ffffff',
				itemTextColorActiveHover: '#ffffff',
				itemBorder: `1px solid ${colors.borderColor}`,
				itemBorderHover: `1px solid ${colors.primaryColorHover}`,
				itemBorderPressed: `1px solid ${colors.primaryColorPressed}`,
				itemBorderActive: `1px solid ${colors.primaryColor}`,
				itemBorderActiveHover: `1px solid ${colors.primaryColorHover}`
			},

			// Badge component
			Badge: {
				color: colors.errorColor,
				textColor: '#ffffff',
				fontWeight: '600'
			},

			// Collapse component
			Collapse: {
				titleTextColor: colors.textColor1,
				textColor: colors.textColor1,
				arrowColor: colors.textColor3,
				arrowColorHover: colors.textColor2,
				arrowColorActive: colors.primaryColor,
				itemMargin: '16px 0 0 0'
			},

			// DatePicker component
			DatePicker: {
				peers: {
					Input: {
						color: colors.inputColor,
						colorDisabled: colors.inputColorDisabled,
						colorFocus: colors.inputColor,
						textColor: colors.textColor1,
						textColorDisabled: colors.textColorDisabled,
						placeholderColor: colors.placeholderColor,
						placeholderColorDisabled: colors.textColorDisabled,
						border: `1px solid ${colors.borderColor}`,
						borderHover: `1px solid ${colors.primaryColorHover}`,
						borderDisabled: `1px solid ${colors.borderColor}`,
						borderFocus: `1px solid ${colors.primaryColor}`,
						boxShadowFocus: `0 0 0 2px ${colors.primaryColor}40`
					}
				}
			},

			// Drawer component
			Drawer: {
				color: colors.modalColor,
				textColor: colors.textColor1,
				borderColor: colors.borderColor,
				boxShadow: isDark
					? '0 6px 16px -9px rgba(0, 0, 0, 0.24), 0 9px 28px 0 rgba(0, 0, 0, 0.15), 0 12px 48px 16px rgba(0, 0, 0, 0.09)'
					: '0 6px 16px -9px rgba(0, 0, 0, 0.08), 0 9px 28px 0 rgba(0, 0, 0, 0.05), 0 12px 48px 16px rgba(0, 0, 0, 0.03)'
			},

			// Upload component
			Upload: {
				draggerColor: colors.hoverColor,
				draggerColorHover: colors.hoverColor,
				draggerBorder: `1px dashed ${colors.borderColor}`,
				draggerBorderHover: `1px dashed ${colors.primaryColor}`,
				draggerTextColor: colors.textColor2,
				fileColor: colors.cardColor,
				fileColorHover: colors.hoverColor,
				fileBorderColor: colors.borderColor,
				fileTextColor: colors.textColor1
			},

			// Checkbox component
			Checkbox: {
				colorChecked: colors.primaryColor,
				colorDisabled: colors.inputColorDisabled,
				colorDisabledChecked: colors.borderColor,
				textColor: colors.textColor1,
				textColorDisabled: colors.textColorDisabled,
				borderColor: colors.borderColor,
				borderColorChecked: colors.primaryColor,
				borderColorDisabled: colors.borderColor,
				borderColorDisabledChecked: colors.borderColor,
				checkMarkColor: '#ffffff',
				checkMarkColorDisabled: colors.textColorDisabled,
				checkMarkColorDisabledChecked: colors.textColorDisabled
			},

			// Scrollbar component
			Scrollbar: {
				color: colors.scrollbarColor,
				colorHover: colors.scrollbarColorHover
			},

			// Message component
			Message: {
				textColor: colors.textColor1,
				color: colors.cardColor,
				boxShadow: isDark
					? '0 3px 6px -4px rgba(0, 0, 0, 0.36), 0 6px 16px 0 rgba(0, 0, 0, 0.24), 0 9px 28px 8px rgba(0, 0, 0, 0.15)'
					: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)'
			},

			// Notification component
			Notification: {
				color: colors.cardColor,
				textColor: colors.textColor1,
				titleTextColor: colors.textColor1,
				boxShadow: isDark
					? '0 3px 6px -4px rgba(0, 0, 0, 0.36), 0 6px 16px 0 rgba(0, 0, 0, 0.24), 0 9px 28px 8px rgba(0, 0, 0, 0.15)'
					: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)'
			}
		}
	}
}

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters
}
